import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, filter, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  locationCodesClearedMessage,
  LOCATION_UPLOAD_MESSAGES,
  locationUploadRejectedMessage,
  locationUploadSuccessMessage,
  QUALIFICATION_CODE,
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SurveyLocationUploadResult,
} from '@ps/survey-utils';
import {
  UserSettingsActionsFacade,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';
import {
  SurveyActivityLogActions,
  SurveyService,
} from '@ps/survey-data-access';
import { TagsActionsFacade } from '@ps/tags-data-access';

import { SurveyTagsService } from '../../services/survey-tags/survey-tags.service';
import { LocationUploadService } from '../../services/location/location-upload.service';
import { SurveyProcessingService } from '../../services/survey-processing/survey-processing.service';

import {
  SurveyCreationActions,
  SurveyCreationApiActions,
} from './survey-creation-audience.actions';
import {
  selectLocationFileUpload,
  selectMetadataForm,
  selectQualificationsForm,
  selectSurvey,
  selectSurveyId,
} from './survey-creation-audience.selectors';

@Injectable()
export class SurveyCreationAudienceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);

  private readonly _surveyService = inject(SurveyService);
  private readonly _surveyProcessingService = inject(SurveyProcessingService);
  private readonly _locationUploadService = inject(LocationUploadService);
  private readonly _surveyTagsService = inject(SurveyTagsService);

  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _userSettingsActions = inject(UserSettingsActionsFacade);
  private readonly _tagsActions = inject(TagsActionsFacade);

  public readonly updatedSurveyQualificationsValidation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.updateQualifications),
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectQualificationsForm),
        ]),
        filter(([, surveyId]) => !!surveyId),
        tap(([, , qualifications]) => {
          Option.from(qualifications.errors[0]?.message).inspect((message) =>
            this._toastService.error(message, { keepOpen: false }),
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly loadTagsCatalog$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.init),
        filter(
          () => !!this._userSettingsSelectors.buyerSettings().enableSurveyTags,
        ),
        tap(() => this._tagsActions.loadTags()),
      ),
    { dispatch: false },
  );

  public readonly loadSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.init),
      filter(
        (action): action is typeof action & { surveyId: number } =>
          !!action.surveyId,
      ),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      // Idempotence guard: skip the fetch when this exact survey is already in
      // memory. Navigations into the edit page reset first, so this only
      // absorbs a repeated init for the survey already loaded.
      filter(([{ surveyId }, survey]) => !survey || survey.id !== surveyId),
      switchMap(([{ surveyId }]) =>
        this._surveyService.getByIdTargeting(surveyId).pipe(
          map((survey) =>
            SurveyCreationApiActions.loadSurveyForEditSuccess({ survey }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationApiActions.loadSurveyForEditFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly uploadLocationCodes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.uploadLocationCodes),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      mergeMap(
        ([
          { countryCode, qualificationCode, qualificationName, file },
          surveyId,
        ]) =>
          this._locationUploadService
            .uploadLocationCodes(
              countryCode,
              surveyId ?? 0,
              qualificationCode,
              file,
            )
            .pipe(
              tap((result) =>
                this._notifyLocationUpload(
                  result,
                  qualificationCode,
                  qualificationName,
                ),
              ),
              map((result) =>
                SurveyCreationApiActions.uploadLocationCodesSuccess({
                  qualificationCode,
                  qualificationName,
                  result,
                }),
              ),
              this.psCatchError(
                (error) =>
                  of(
                    SurveyCreationApiActions.uploadLocationCodesFailure({
                      qualificationCode,
                      error:
                        error.message || LOCATION_UPLOAD_MESSAGES.UPLOAD_FAILED,
                    }),
                  ),
                { notify: true },
              ),
            ),
      ),
    ),
  );

  public readonly notifyLocationCodesCleared$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.clearLocationCodes),
        tap(({ qualificationName }) => {
          this._toastService.success(
            locationCodesClearedMessage(qualificationName),
            { keepOpen: false },
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly downloadLocationCodesTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.downloadLocationCodesTemplate),
        switchMap(({ countryCode, qualificationCode }) =>
          this._locationUploadService
            .downloadTemplateFile(countryCode, qualificationCode)
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  public readonly downloadValidatedLocationFile$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationActions.downloadValidatedLocationFile),
        concatLatestFrom(() => this._store.select(selectLocationFileUpload)),
        map(([{ qualificationCode }, upload]) =>
          upload.qualificationCode === qualificationCode
            ? Option.from(upload.result)
            : Option.none,
        ),
        filter((upload) => upload.isSome()),
        switchMap((upload) => {
          const result = upload.unwrap();

          return this._locationUploadService
            .downloadValidatedFile(result.filePath, result.validCodes)
            .pipe(this.psCatchError(() => EMPTY, { notify: true }));
        }),
      ),
    { dispatch: false },
  );

  public readonly loadSurveyTagsForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationApiActions.loadSurveyForEditSuccess),
      filter(
        () => !!this._userSettingsSelectors.buyerSettings().enableSurveyTags,
      ),
      switchMap(({ survey }) =>
        this._surveyTagsService.getSurveyTags(survey.id).pipe(
          map((tagIds) =>
            SurveyCreationApiActions.loadSurveyTagsSuccess({ tagIds }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationApiActions.loadSurveyTagsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly linkSurveyTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.createStandardSurveySuccess,
        SurveyCreationApiActions.saveSurveySuccess,
      ),
      filter(
        () => !!this._userSettingsSelectors.buyerSettings().enableSurveyTags,
      ),
      concatLatestFrom(() => this._store.select(selectMetadataForm)),
      map(([{ survey }, metadata]) => ({
        surveyId: survey.id,
        selectedTags: Option.from(metadata.value)
          .map((value) => value.selectedTags)
          .unwrapOr([]),
      })),
      switchMap(({ surveyId, selectedTags }) =>
        this._surveyTagsService.linkSurveyTags(surveyId, selectedTags).pipe(
          map(() => SurveyCreationApiActions.linkSurveyTagsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationApiActions.linkSurveyTagsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly toggleManualProcessing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.toggleManualProcessing),
      filter(() =>
        this._userSettingsSelectors.buyerConfigSettingsId().isSome(),
      ),
      switchMap(({ companyId, enabled }) => {
        const buyerConfigSettingsId = this._userSettingsSelectors
          .buyerConfigSettingsId()
          .unwrap();

        return this._surveyProcessingService
          .updateManualProcessing(buyerConfigSettingsId, companyId, enabled)
          .pipe(
            tap(() => {
              this._toastService.success(
                'Processing setting updated successfully',
              );
            }),
            tap(() => {
              this._userSettingsActions.updateBuyerSetting(
                'enableManualProcessing',
                companyId,
                enabled,
              );
            }),
            switchMap(() => {
              const logAction = SurveyActivityLogActions.logOperatorActivity({
                log: {
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.manualProcessingToggled,
                  metadata: { action: enabled ? 'ON' : 'OFF' },
                },
              });

              return of(logAction);
            }),
            this.psCatchError(() => EMPTY, { notify: true }),
          );
      }),
    ),
  );

  private _notifyLocationUpload(
    result: SurveyLocationUploadResult,
    qualificationCode: number,
    qualificationName: string,
  ): void {
    if (!result.validCodes.length) {
      this._toastService.error(
        qualificationCode === QUALIFICATION_CODE.FSA
          ? LOCATION_UPLOAD_MESSAGES.INVALID_FSA_CODE_FILE
          : LOCATION_UPLOAD_MESSAGES.INVALID_LOCATION_CODE_FILE,
        { keepOpen: false },
      );
      return;
    }

    if (result.incorrectCodeCount) {
      this._toastService.warning(
        locationUploadRejectedMessage(
          result.incorrectCodeCount,
          result.incorrectCodes,
        ),
        { keepOpen: false },
      );
    }

    this._toastService.success(
      locationUploadSuccessMessage({
        correctCodeCount: result.correctCodeCount,
        totalCodeCount: result.totalCodeCount,
        duplicateCodeCount: result.duplicateCodeCount,
        modifiedCodeCount: result.modifiedCodeCount,
        qualificationName,
      }),
      { keepOpen: false },
    );
  }
}
